import type { CourtFeed } from './eventFeeds';
import type { MatchLocator } from './matchLocators';

export type WatchRowKind = 'live' | 'video' | 'link' | 'none';

export type WatchRow<M> = {
  match: M;
  kind: WatchRowKind;
  /** The feed a click plays; null for `link` and `none` rows. */
  feedId: string | null;
  offsetSec: number;
};

/**
 * One court-day's matches as list rows, forward by `scheduledAt`. Per row, in order:
 * live (in progress and the court's first feed is a live app feed), video (a locator for its
 * matchId), link (a matchId with no locator and a pasted link on the court that day), none
 * (a null matchId row is `none`, except one in progress on a live app feed, which is `live`). `courtFeeds` is
 * `feedsForCourtDay` output, so its first entry is the first feed. Pure.
 */
export function watchRows<
  M extends { id: string; matchId: string | null; courtId: string; scheduledAt: string },
>(
  matches: M[],
  courtFeeds: CourtFeed[],
  locators: MatchLocator[],
  inProgressIds: Set<string>,
): WatchRow<M>[] {
  const first = courtFeeds[0];
  const liveFeed = first && first.source === 'app' && first.appState === 'live' ? first : null;
  const hasLink = courtFeeds.some((feed) => feed.source === 'link');
  const byMatch = new Map(locators.map((locator) => [locator.matchId, locator]));
  return [...matches]
    .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt))
    .map((match): WatchRow<M> => {
      if (liveFeed && inProgressIds.has(match.id)) {
        return { match, kind: 'live', feedId: liveFeed.id, offsetSec: 0 };
      }
      const locator = match.matchId ? byMatch.get(match.matchId) : undefined;
      if (locator) {
        return { match, kind: 'video', feedId: locator.feedId, offsetSec: locator.offsetSec };
      }
      // A row with no match has nothing to show: no Scorecard, no player change.
      return {
        match,
        kind: match.matchId && hasLink ? 'link' : 'none',
        feedId: null,
        offsetSec: 0,
      };
    });
}

/**
 * The row to mark when the viewer has not picked one: the live row; else null when the court's
 * first feed is live (it plays from the live edge with no row marked, so a finished match never
 * drops a live viewer onto a replay); else the last row with video; else null. Pure.
 */
export function defaultRow<M>(
  rows: WatchRow<M>[],
  firstFeedLive: boolean,
): WatchRow<M> | null {
  const live = rows.find((row) => row.kind === 'live');
  if (live) return live;
  if (firstFeedLive) return null;
  for (let i = rows.length - 1; i >= 0; i--) {
    if (rows[i].kind === 'video') return rows[i];
  }
  return null;
}

/**
 * The row the panel under the video describes. The selected row when there is one. With none
 * selected and the court's first feed live, the viewer is on the live feed with no row marked, so
 * the panel keeps the last finished row that has a match record: the match that just ended, and
 * between matches the one before. `cleared` is a viewer who chose a camera, who gets no panel.
 * `isFinished` is the caller's (a played, completed match; winnerless rows are not). Pure.
 */
export function panelRow<M extends { matchId: string | null }>(
  selected: WatchRow<M> | null,
  rows: WatchRow<M>[],
  firstFeedLive: boolean,
  cleared: boolean,
  isFinished: (match: M) => boolean,
): WatchRow<M> | null {
  if (selected) return selected;
  if (cleared || !firstFeedLive) return null;
  for (let i = rows.length - 1; i >= 0; i--) {
    const { match } = rows[i];
    if (match.matchId && isFinished(match)) return rows[i];
  }
  return null;
}

/**
 * The schedule's matches for `day`. `getPublicSchedule` falls back to another day when the asked
 * day has none, so a schedule that answered for a different day contributes no rows. Pure.
 */
export function scheduleRowsForDay<M>(
  schedule: { selectedDay: string; matches: M[] },
  day: string,
): M[] {
  return schedule.selectedDay === day ? schedule.matches : [];
}

/**
 * The start second for a `video` row. YouTube lands on the live moment for `start=0` on a feed
 * that is still live, so a match at offset 0 there starts at 1. Pure.
 */
export function playStart(offsetSec: number, feedLive: boolean): number {
  return feedLive && offsetSec <= 0 ? 1 : offsetSec;
}
